import { useEffect, useMemo, useState, type FormEvent } from "react";
import { TodoCard } from "../../components/TodoCard";
import { ProjectFiles } from "../../components/ProjectFiles";
import { addDoc, collection, doc, getDoc, onSnapshot, orderBy, query, updateDoc, where } from "firebase/firestore";
import { Link, useLocation, useRoute } from "wouter";
import { MapPin } from "lucide-react";
import { lotKey, paths, ROLE_RANK, type Estimate, type Invoice, type Job, type Project } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { BUCKETS, BUCKET_LABEL, computePipeline } from "../../lib/pipeline";
import { textMatches } from "../../lib/filter";
import { useAuth } from "../../hooks/useAuth";
import { useCustomerList } from "../../hooks/useCustomerList";
import { CustomerPicker } from "../../components/CustomerPicker";
import { JobsCalendar } from "../../components/JobsCalendar";
import { PinDrop } from "../../components/PinDrop";
import { ProjectItemsPanel, useProjectItems } from "../../components/ProjectItems";
import { AddLots, LinkLotsBanner, LotDetails } from "../../components/LotPanel";
import { LienNotices } from "../../components/LienNotices";
import { useProjectLots, type LotRow } from "../../lib/lots";
import { AddressField, locatedOf, type Located } from "../../components/AddressField";
import { SendDocuments } from "../../components/SendDocuments";
import { useAccess } from "../../hooks/useAccess";

// Projects: a builder's development (KB Homes → Desert Skies 25's), a hotel
// remodel, a 30-lot release. Customer → project → lots → jobs. A project page
// answers "how is the whole thing going?": every lot and job, a calendar, and
// the money.

type ProjRow = Project & { id: string };
type JobRow = Job & { id: string };

const money = (c: number) =>
  (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const shortDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
const STATUS_LABEL: Record<Job["status"], string> = { scheduled: "scheduled", in_progress: "in progress", done: "done", canceled: "canceled" };

export function ProjectsPage() {
  const [, params] = useRoute("/projects/:id");
  if (params?.id) return <ProjectDetail projectId={params.id} />;
  return <ProjectList />;
}

function ProjectList() {
  const { tenantId } = useAuth();
  const { rows: customers } = useCustomerList(tenantId);
  const [projects, setProjects] = useState<ProjRow[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", customerId: "", customerName: "" });
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"active" | "closed" | "all">("active");
  const [limit, setLimit] = useState(200);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(query(collection(db, paths.tenantProjects(tenantId)), orderBy("createdAt", "desc")), (snap) =>
      setProjects(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Project) }))),
    );
  }, [tenantId]);

  const names = useMemo(() => new Map(customers.map((c) => [c.id, c.displayName])), [customers]);
  const customerName = (id: string | null) => (id ? (names.get(id) ?? "") : "");
  const shown = projects.filter(
    (p) => (status === "all" || p.status === status) && textMatches(search, p.name, customerName(p.customerId), p.address),
  );

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!tenantId || !form.name.trim()) return;
    setBusy(true);
    try {
      const project: Project = {
        name: form.name.trim(),
        customerId: form.customerId || null,
        status: "active",
        note: "",
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      await addDoc(collection(db, paths.tenantProjects(tenantId)), project);
      setForm({ name: "", customerId: "", customerName: "" });
      setShowCreate(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Projects</h1>
        <Button onClick={() => setShowCreate((v) => !v)}>{showCreate ? "Cancel" : "New project"}</Button>
      </div>
      <p className="text-xs text-steel">
        A project holds everything for one development or engagement: a builder's subdivision, a hotel remodel.
        Open a project for its lots, jobs and calendar. Simple service calls don't need one.
      </p>

      {showCreate && tenantId ? (
        <Card>
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Tobiano" />
            </div>
            <div>
              <Label>Customer (builder / GC)</Label>
              <CustomerPicker
                tenantId={tenantId}
                value={form.customerId}
                valueName={form.customerName}
                onChange={(c) => setForm((f) => ({ ...f, customerId: c?.id ?? "", customerName: c?.displayName ?? "" }))}
              />
            </div>
            <div>
              <Button type="submit" busy={busy}>
                Create
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Input className="min-w-48 flex-1" placeholder="Search projects or builders…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select className="w-auto py-1.5 text-xs" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="active">Active</option>
          <option value="closed">Closed</option>
          <option value="all">All</option>
        </Select>
      </div>

      <Card className="p-0">
        {shown.slice(0, limit).map((p) => (
          <Link
            key={p.id}
            href={`/projects/${p.id}`}
            className="flex items-center justify-between border-b border-steel/10 px-4 py-2 last:border-0 hover:bg-cream/60"
          >
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-ink">{p.name}</span>
              <span className="block truncate text-xs text-steel">
                {customerName(p.customerId) || "No customer"}
                {p.address ? ` · ${p.address}` : ""}
              </span>
            </span>
            <span
              className={cn(
                "ml-2 shrink-0 rounded-full px-2 py-0.5 text-xs",
                p.status === "active" ? "bg-success/15 text-success" : "bg-steel/15 text-steel",
              )}
            >
              {p.status}
            </span>
          </Link>
        ))}
        {shown.length > limit ? (
          <button className="block w-full px-4 py-2 text-center text-xs text-steel hover:text-ink" onClick={() => setLimit((n) => n + 200)}>
            Show more ({shown.length - limit} left)
          </button>
        ) : null}
        {shown.length === 0 ? <p className="px-4 py-8 text-center text-sm text-steel">No projects match.</p> : null}
      </Card>
    </div>
  );
}

/** Jobs with no lot record group by their lot text, else street address, else "no lot yet". */
const looseKey = (j: JobRow) => (j.lot?.trim() ? `lot:${lotKey(j.lot)}` : j.address?.trim() ? `addr:${j.address.trim().toLowerCase()}` : "");
type LotGroup = { key: string; lot: LotRow | null; label: string; address: string; jobs: JobRow[] };

function ProjectDetail({ projectId }: { projectId: string }) {
  const { tenantId, role, user } = useAuth();
  const [, navigate] = useLocation();
  const canEdit = !!role && ROLE_RANK[role] >= ROLE_RANK.manager;
  const can = useAccess();
  const [project, setProject] = useState<Project | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [invoices, setInvoices] = useState<Array<Invoice & { id: string }>>([]);
  const [estimates, setEstimates] = useState<Array<Estimate & { id: string }>>([]);
  const [view, setView] = useState<"lots" | "items" | "calendar" | "documents">("lots");
  const [openLot, setOpenLot] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", note: "", qbName: "" });
  const [loc, setLoc] = useState<Located>(null);
  const isOffice = !!role && role !== "tech";
  const lotRecords = useProjectLots(tenantId, projectId, isOffice);
  const projectItems = useProjectItems(isOffice ? tenantId : null, projectId);
  const [addingLots, setAddingLots] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(doc(db, paths.tenantProject(tenantId, projectId)), (snap) => setProject((snap.data() as Project) ?? null)),
      onSnapshot(query(collection(db, paths.tenantJobs(tenantId)), where("projectId", "==", projectId)), (snap) =>
        setJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) }))),
      ),
      ...(role !== "tech"
        ? [
            onSnapshot(query(collection(db, paths.tenantInvoices(tenantId)), where("projectId", "==", projectId)), (snap) =>
              setInvoices(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Invoice) }))),
            ),
            onSnapshot(query(collection(db, paths.tenantEstimates(tenantId)), where("projectId", "==", projectId)), (snap) =>
              setEstimates(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Estimate) }))),
            ),
          ]
        : []),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId, projectId, role]);

  useEffect(() => {
    if (!tenantId || !project?.customerId) {
      setCustomerName("");
      setCustomerEmail("");
      return;
    }
    void getDoc(doc(db, paths.tenantCustomer(tenantId, project.customerId))).then((s) => {
      setCustomerName((s.get("displayName") as string | undefined) ?? "");
      setCustomerEmail((s.get("email") as string | undefined) ?? "");
    });
  }, [tenantId, project?.customerId]);

  const rootJobs = useMemo(() => jobs.filter((j) => !j.parentJobId), [jobs]);
  const liveInvoices = invoices.filter((i) => i.status !== "void");
  const buckets = useMemo(() => computePipeline(jobs, liveInvoices), [jobs, liveInvoices]);
  const billed = liveInvoices.reduce((s, i) => s + i.totalCents, 0);
  const collected = liveInvoices.reduce((s, i) => s + i.amountPaidCents, 0);
  const doneCount = rootJobs.filter((j) => j.status === "done").length;

  // Lot records first (even ones with no jobs yet), then jobs that only name a lot.
  const lots = useMemo(() => {
    const m = new Map<string, LotGroup>();
    const byId = new Map(lotRecords.map((l) => [l.id, l]));
    const byKey = new Map(lotRecords.map((l) => [l.key, l]));
    for (const l of lotRecords) m.set(`id:${l.id}`, { key: `id:${l.id}`, lot: l, label: l.name, address: l.address, jobs: [] });
    for (const j of rootJobs) {
      const rec = (j.lotId ? byId.get(j.lotId) : undefined) ?? (j.lot?.trim() ? byKey.get(lotKey(j.lot)) : undefined);
      const k = rec ? `id:${rec.id}` : looseKey(j);
      const entry = m.get(k) ?? m.set(k, { key: k, lot: null, label: j.lot?.trim() || j.address?.trim() || "No lot or address yet", address: "", jobs: [] }).get(k)!;
      if (!entry.lot && j.lot?.trim() && j.address?.trim() && !entry.address) entry.address = j.address.trim();
      entry.jobs.push(j);
    }
    const list: LotGroup[] = [];
    for (const g of m.values()) {
      // A lot that matches the search shows all its jobs; otherwise only the matching jobs.
      const lotHit = textMatches(search, g.label, g.address, g.lot?.homeownerName, g.lot?.projectItemName, g.lot?.note);
      const jobs = lotHit ? g.jobs : g.jobs.filter((x) => textMatches(search, x.title, x.lot, x.address, x.status, x.poNumber));
      if (!lotHit && jobs.length === 0) continue;
      list.push({ ...g, jobs: [...jobs].sort((a, b) => (b.startAt ?? 0) - (a.startAt ?? 0)) });
    }
    return list.sort((a, b) => (a.key === "" ? 1 : b.key === "" ? -1 : a.label.localeCompare(b.label, undefined, { numeric: true })));
  }, [rootJobs, lotRecords, search]);
  const unlinked = isOffice ? jobs.filter((j) => !j.lotId && j.lot?.trim()).length : 0;
  // Lots are optional: a remodel or a residence is just jobs.
  const hasLots = lotRecords.length > 0 || rootJobs.some((j) => !!j.lot?.trim());

  async function save(patch: Partial<Project>) {
    if (!tenantId) return;
    await updateDoc(doc(db, paths.tenantProject(tenantId, projectId)), { ...patch, updatedAt: Date.now() });
  }

  if (!project) return <p className="text-sm text-steel">Loading project…</p>;
  const pinned = project.lat != null && project.lng != null;
  const mapsUrl = pinned
    ? `https://maps.google.com/?q=${project.lat},${project.lng}`
    : project.address
      ? `https://maps.google.com/?q=${encodeURIComponent(project.address)}`
      : null;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-steel">
            {project.customerId ? (
              <button className="hover:text-ink" onClick={() => navigate(`/customers/${project.customerId}`)}>
                ← {customerName || "Customer"}
              </button>
            ) : (
              <button className="hover:text-ink" onClick={() => navigate("/projects")}>
                ← Projects
              </button>
            )}
          </div>
          <h1 className="truncate text-lg font-bold">{project.name}</h1>
          <p className="text-xs text-steel">
            {hasLots ? `${lots.filter((l) => l.lot || l.key.startsWith("lot:")).length} lots · ` : ""}
            {doneCount} of {rootJobs.length} jobs done
            {rootJobs.length > 0 ? ` · ${Math.round((100 * doneCount) / rootJobs.length)}%` : ""}
          </p>
        </div>
        {canEdit ? (
          <div className="flex shrink-0 flex-col items-end gap-1">
            <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => void save({ status: project.status === "active" ? "closed" : "active" })}>
              {project.status === "active" ? "Close project" : "Reopen"}
            </Button>
            {!editing ? (
              <button
                className="text-xs text-steel underline hover:text-ink"
                onClick={() => {
                  setForm({ name: project.name, address: project.address ?? "", note: project.note, qbName: project.qbName ?? "" });
                  setLoc(locatedOf(project));
                  setEditing(true);
                }}
              >
                Edit project
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <Card>
        {editing ? (
          <form
            className="grid gap-2 sm:grid-cols-2"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!form.name.trim()) return;
              await save({
                name: form.name.trim(),
                address: form.address.trim(),
                note: form.note.trim(),
                qbName: form.qbName.trim().slice(0, 41),
                ...(loc ? { lat: loc.lat, lng: loc.lng } : {}),
              });
              setEditing(false);
            }}
          >
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Address (once there is one)</Label>
              <AddressField value={form.address} onChange={(address) => setForm((f) => ({ ...f, address }))} located={loc} onLocate={setLoc} />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes (gate codes, superintendent, …)</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>QuickBooks name (the sub-customer under {customerName || "the builder"}; blank = the project name)</Label>
              <Input value={form.qbName} onChange={(e) => setForm({ ...form, qbName: e.target.value })} maxLength={41} placeholder={form.name} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="px-3 py-1 text-xs">
                Save
              </Button>
              <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-steel">Location</p>
              {mapsUrl ? (
                <a href={mapsUrl} target="_blank" rel="noreferrer" className="mt-0.5 flex items-start gap-1 text-sm text-brandorange-dark">
                  <MapPin size={14} className="mt-0.5 shrink-0" /> {project.address || "Pinned on the map"}
                </a>
              ) : (
                <p className="text-sm text-steel">No address yet. Drop a pin on the development.</p>
              )}
              {canEdit ? (
                <button className="mt-1 text-xs text-brandorange-dark underline hover:text-brandorange" onClick={() => setPinOpen((v) => !v)}>
                  {pinOpen ? "Close map" : pinned ? "Adjust pin" : "Drop a pin"}
                </button>
              ) : null}
              {pinned && !pinOpen ? <span className="ml-2 text-[11px] text-success">pinned ✓</span> : null}
            </div>
            {project.note ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-steel">Notes</p>
                <p className="whitespace-pre-wrap text-sm text-ink">{project.note}</p>
              </div>
            ) : null}
            {canEdit && customerName ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-steel">In QuickBooks</p>
                <p className="text-sm text-ink">
                  {customerName}:{project.qbName?.trim() || project.name}
                </p>
              </div>
            ) : null}
          </div>
        )}
        {pinOpen ? (
          <div className="mt-3 border-t border-steel/15 pt-3">
            <PinDrop
              initial={pinned ? { lat: project.lat!, lng: project.lng! } : null}
              home={null}
              onSave={async (pin) => {
                await save({ lat: pin.lat, lng: pin.lng });
                setPinOpen(false);
              }}
              onCancel={() => setPinOpen(false)}
            />
          </div>
        ) : null}
      </Card>

      {role !== "tech" ? (
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-cream px-3 py-2 text-center">
            <div className="text-base font-bold text-ink">{rootJobs.length}</div>
            <div className="text-[11px] text-steel">Jobs</div>
          </div>
          <div className="rounded-lg bg-cream px-3 py-2 text-center">
            <div className="text-base font-bold text-ink">{money(billed)}</div>
            <div className="text-[11px] text-steel">Billed</div>
          </div>
          <div className="rounded-lg bg-cream px-3 py-2 text-center">
            <div className="text-base font-bold text-ink">{money(collected)}</div>
            <div className="text-[11px] text-steel">Collected</div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        {BUCKETS.map((b) => {
          const n = buckets.get(b)?.length ?? 0;
          if (n === 0) return null;
          return (
            <span key={b} className="rounded-full bg-steel/10 px-2.5 py-1 text-xs text-ink">
              {BUCKET_LABEL[b]}: <strong>{n}</strong>
            </span>
          );
        })}
      </div>

      {tenantId && role !== "tech" && can("customers.senddocs") ? (
        <SendDocuments tenantId={tenantId} customerId={project.customerId} projectId={projectId} suggestedEmails={customerEmail ? [customerEmail] : []} />
      ) : null}

      {tenantId && role !== "tech" ? <ProjectFiles tenantId={tenantId} projectId={projectId} /> : null}
      {tenantId && isOffice ? (
        <LienNotices tenantId={tenantId} projectId={projectId} project={project} jobs={jobs} lots={lotRecords} customerName={customerName} canEdit={canEdit} />
      ) : null}
      {tenantId && role !== "tech" ? <TodoCard tenantId={tenantId} link={{ kind: "project", id: projectId, label: project.name.slice(0, 200) }} title="To-dos for this project" /> : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-steel/30 p-0.5 text-xs">
          {(["lots", ...(role !== "tech" ? (["items"] as const) : []), "calendar", ...(role !== "tech" ? (["documents"] as const) : [])] as const).map((v) => (
            <button
              key={v}
              className={cn("rounded-md px-3 py-1", view === v ? "bg-ink text-white" : "text-steel")}
              onClick={() => setView(v)}
            >
              {v === "lots" ? (hasLots ? "Lots & jobs" : "Jobs") : v === "items" ? "Items & bids" : v === "calendar" ? "Calendar" : "Estimates & invoices"}
            </button>
          ))}
        </div>
        {view === "lots" ? (
          <Input className="min-w-40 flex-1" placeholder="Find a lot, address, homeowner, PO or job…" value={search} onChange={(e) => setSearch(e.target.value)} />
        ) : null}
        {view === "lots" && isOffice ? (
          <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => setAddingLots((v) => !v)}>
            {addingLots ? "Done adding" : "Add lots"}
          </Button>
        ) : null}
      </div>

      {view === "lots" ? (
        <Card className="p-0">
          {addingLots && tenantId ? <AddLots tenantId={tenantId} projectId={projectId} lots={lotRecords} items={projectItems} /> : null}
          {canEdit && tenantId ? <LinkLotsBanner tenantId={tenantId} projectId={projectId} count={unlinked} /> : null}
          {lots.map((l) => {
            const open = openLot === l.key || lots.length === 1;
            const openJobs = l.jobs.filter((j) => j.status === "scheduled" || j.status === "in_progress");
            const next = openJobs.filter((j) => j.startAt).sort((a, b) => a.startAt! - b.startAt!)[0];
            return (
              <div key={l.key || "none"} className="border-b border-steel/10 last:border-0">
                <button className="flex w-full items-center justify-between gap-2 px-4 py-2 text-left hover:bg-cream/60" onClick={() => setOpenLot(open ? null : l.key)}>
                  <span className="min-w-0">
                    <span className={cn("block truncate text-sm font-semibold", l.key ? "text-ink" : "text-steel")}>
                      {l.label}
                      {l.lot?.status === "closed" ? <span className="ml-1.5 rounded-full bg-steel/15 px-1.5 text-[10px] font-normal text-steel">closed out</span> : null}
                    </span>
                    {l.address || l.lot?.projectItemName || l.lot?.homeownerName ? (
                      <span className="block truncate text-xs text-steel">{[l.address, l.lot?.projectItemName, l.lot?.homeownerName].filter(Boolean).join(" · ")}</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-xs text-steel">
                    {openJobs.length > 0 ? `${openJobs.length} open · ` : ""}
                    {l.jobs.length ? `${l.jobs.length} job${l.jobs.length === 1 ? "" : "s"}` : "no jobs yet"}
                    {next ? ` · next ${shortDay(next.startAt!)}` : ""} {open ? "▾" : "▸"}
                  </span>
                </button>
                {open ? (
                  <div className="bg-cream/30 pb-1">
                    {l.lot && tenantId ? (
                      <LotDetails
                        key={l.lot.id}
                        tenantId={tenantId}
                        projectId={projectId}
                        lot={l.lot}
                        jobs={l.jobs}
                        items={projectItems}
                        canEdit={isOffice}
                        canRemove={canEdit}
                        user={user}
                      />
                    ) : null}
                    {l.jobs.map((j) => (
                      <button
                        key={j.id}
                        className="flex w-full items-center justify-between gap-2 px-6 py-1 text-left text-sm hover:bg-cream"
                        onClick={() => navigate(`/jobs/${j.id}`)}
                      >
                        <span className="min-w-0 truncate text-ink">{j.title}</span>
                        <span className="shrink-0 text-xs text-steel">
                          {j.startAt ? shortDay(j.startAt) : "no date"} · {STATUS_LABEL[j.status]}
                        </span>
                      </button>
                    ))}
                    {isOffice && (l.lot || l.key.startsWith("lot:")) ? (
                      <Link
                        href={`/jobs?project=${encodeURIComponent(projectId)}&lot=${encodeURIComponent(l.lot?.name ?? l.label)}`}
                        className="block px-6 py-1 text-xs text-brandorange-dark hover:underline"
                      >
                        + New job for {l.lot?.name ?? l.label}
                      </Link>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
          {lots.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-steel">
              {rootJobs.length === 0 ? (isOffice ? "No lots or jobs yet. Add the lots, or create a job on the Jobs page and pick this project." : "No jobs yet.") : "Nothing matches."}
            </p>
          ) : null}
        </Card>
      ) : null}

      {view === "items" && tenantId && user ? (
        <ProjectItemsPanel
          tenantId={tenantId}
          projectId={projectId}
          customerId={project.customerId}
          uid={user.uid}
          canEdit={canEdit}
          jobs={jobs}
          invoices={invoices}
          onOpenJob={(id) => navigate(`/jobs/${id}`)}
        />
      ) : null}

      {view === "calendar" ? (
        <Card>
          <JobsCalendar
            jobs={jobs.map((j) => ({ id: j.id, title: j.title, startAt: j.startAt, status: j.status, lot: j.lot }))}
            onOpen={(id) => navigate(`/jobs/${id}`)}
          />
        </Card>
      ) : null}

      {view === "documents" ? (
        <div className="space-y-3">
          <Card className="p-0">
            <CardTitle className="px-4 pt-3">Estimates ({estimates.length})</CardTitle>
            {[...estimates]
              .sort((a, b) => b.createdAt - a.createdAt)
              .map((e) => (
                <div key={e.id} className="flex items-center justify-between border-b border-steel/10 px-4 py-1.5 text-sm last:border-0">
                  <span className="text-ink">#{e.number}</span>
                  <span className="text-xs text-steel">
                    {shortDay(e.createdAt)} · {money(e.totalCents)} · {e.status}
                  </span>
                </div>
              ))}
            {estimates.length === 0 ? <p className="px-4 pb-3 text-xs text-steel">None yet.</p> : null}
          </Card>
          <Card className="p-0">
            <CardTitle className="px-4 pt-3">Invoices ({invoices.length})</CardTitle>
            {[...invoices]
              .sort((a, b) => b.createdAt - a.createdAt)
              .map((i) => (
                <div key={i.id} className="flex items-center justify-between border-b border-steel/10 px-4 py-1.5 text-sm last:border-0">
                  <span className="text-ink">#{i.number}</span>
                  <span className="text-xs text-steel">
                    {shortDay(i.createdAt)} · {money(i.totalCents)} · {i.status.replace("_", " ")}
                  </span>
                </div>
              ))}
            {invoices.length === 0 ? <p className="px-4 pb-3 text-xs text-steel">None yet.</p> : null}
          </Card>
        </div>
      ) : null}
    </div>
  );
}
